import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 电影赏析 - 横屏「影评分析」风格
 *
 * 核心亮点：
 * - 电影胶片边框 + 遮幅效果
 * - 镜头语言分析卡片
 * - 配色方案提取
 * - 评分仪表 + 导演信息
 * - 影评名句打字效果
 *
 * 尺寸：1920x1080 横屏
 */

interface ShotAnalysis {
  type: string;
  description: string;
  icon: string;
}

interface FilmCinematicTemplateProps {
  filmTitle: string;
  filmTitleEn: string;
  director: string;
  year: string;
  genre: string;
  rating: number;
  palette: { hex: string; name: string }[];
  shots: ShotAnalysis[];
  quote: string;
  themes: string[];
  trivia: string;
}

const DEFAULT_PROPS: FilmCinematicTemplateProps = {
  filmTitle: '星际穿越',
  filmTitleEn: 'Interstellar',
  director: '克里斯托弗·诺兰',
  year: '2014',
  genre: '科幻 / 冒险 / 剧情',
  rating: 9.4,
  palette: [
    { hex: '#0a1628', name: '深空蓝' },
    { hex: '#c8965a', name: '沙漠金' },
    { hex: '#e8e0d0', name: '星光白' },
    { hex: '#2a4a6a', name: '冰河蓝' },
    { hex: '#1a1a1a', name: '黑洞' },
  ],
  shots: [
    { type: '大远景', description: '太空全景展现人类的渺小与宇宙的壮阔', icon: '🌌' },
    { type: '特写镜头', description: '宇航员面罩反射星光，传递孤独与决心', icon: '👤' },
    { type: '主观镜头', description: '穿越虫洞时的第一人称视角，极致沉浸感', icon: '👁️' },
  ],
  quote: '我们曾仰望星空，思索我们在宇宙中的位置；如今我们只低头看脚下，忧虑自己在尘世中的处境。',
  themes: ['父女之爱跨越时空', '人类探索精神的赞歌', '科学与人文的深度融合', '时间相对性的情感表达'],
  trivia: '片中黑洞"卡冈图雅"的视觉效果基于 Kip Thorne 的物理方程渲染，催生了一篇学术论文',
};

export const FilmCinematicTemplate: React.FC<Partial<FilmCinematicTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerProgress = spring({ frame: frame - 10, fps, config: { damping: 18, stiffness: 90 } });
  const letterboxOpen = interpolate(frame, [0, 40], [0.18, 0.05], { extrapolateRight: 'clamp' });

  const paletteStart = 70;
  const shotStart = 130;
  const shotInterval = 50;
  const quoteStart = shotStart + p.shots.length * shotInterval + 10;
  const quoteLen = p.quote.length;
  const quoteChars = Math.floor(interpolate(frame, [quoteStart, quoteStart + quoteLen * 1.5], [0, quoteLen], { extrapolateRight: 'clamp' }));
  const themeStart = quoteStart + quoteLen * 1.5 + 20;
  const triviaStart = themeStart + 80;
  const triviaOpacity = interpolate(frame, [triviaStart, triviaStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  const ratingDisplay = interpolate(frame, [40, 80], [0, p.rating], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill
      style={{
        background: '#0a0a0a',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Film grain texture */}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.015) 1px, transparent 1px)', backgroundSize: '4px 4px' }} />

      {/* Gradient bg from palette */}
      <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, ${p.palette[0]?.hex || '#0a1628'} 0%, #0a0a0a 50%, ${p.palette[3]?.hex || '#2a4a6a'}15 100%)` }} />

      {/* Letterbox bars */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: `${letterboxOpen * 100}%`, background: '#000', zIndex: 10 }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${letterboxOpen * 100}%`, background: '#000', zIndex: 10 }} />

      {/* Film perforation strip (left) */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 36, background: '#111' }}>
        {Array.from({ length: 18 }).map((_, i) => (
          <div key={i} style={{
            position: 'absolute', left: 10, top: 10 + i * 62,
            width: 16, height: 24, borderRadius: 4,
            background: '#0a0a0a', border: '1px solid #222',
          }} />
        ))}
      </div>

      {/* ========== LEFT PANEL ========== */}
      <div style={{ position: 'absolute', top: 70, left: 70, width: 860 }}>
        {/* Header */}
        <div style={{ opacity: headerProgress, transform: `translateY(${(1 - headerProgress) * -12}px)` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
            <div style={{ padding: '6px 18px', background: 'rgba(200,150,90,0.12)', border: '1px solid rgba(200,150,90,0.25)', borderRadius: 10, fontSize: 24, color: '#c8965a', fontWeight: 700 }}>
              🎬 FILM
            </div>
            <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.25)' }}>{p.year} · {p.genre}</span>
          </div>
          <h1 style={{ fontSize: 56, fontWeight: 900, margin: 0 }}>{p.filmTitle}</h1>
          <p style={{ fontSize: 28, color: 'rgba(255,255,255,0.3)', marginTop: 4, fontStyle: 'italic' }}>{p.filmTitleEn}</p>
          <p style={{ fontSize: 26, color: '#c8965a', marginTop: 10 }}>🎥 导演：{p.director}</p>
        </div>

        {/* Color palette */}
        <div style={{ marginTop: 32 }}>
          <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginBottom: 12, fontWeight: 600 }}>影片色调</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {p.palette.map((c, i) => {
              const w = interpolate(frame, [paletteStart + i * 8, paletteStart + i * 8 + 20], [0, 1], { extrapolateRight: 'clamp' });
              return (
                <div key={i} style={{ opacity: w, textAlign: 'center' }}>
                  <div style={{ width: 80, height: 50, borderRadius: 10, background: c.hex, border: '1px solid rgba(255,255,255,0.08)' }} />
                  <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.35)', marginTop: 10 }}>{c.name}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Iconic quote */}
        <div style={{ marginTop: 32, padding: '22px 28px', background: 'rgba(200,150,90,0.05)', border: '1px solid rgba(200,150,90,0.12)', borderRadius: 16 }}>
          <div style={{ fontSize: 26, color: '#c8965a', marginBottom: 8, fontWeight: 600 }}>✍️ 经典台词</div>
          <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.8)', lineHeight: 1.7, fontStyle: 'italic', minHeight: 72 }}>
            「{p.quote.slice(0, quoteChars)}
            {quoteChars < quoteLen && <span style={{ opacity: frame % 20 > 10 ? 1 : 0 }}>|</span>}
            {quoteChars >= quoteLen && '」'}
          </div>
        </div>
      </div>

      {/* ========== RIGHT PANEL ========== */}
      <div style={{ position: 'absolute', top: 70, right: 90, width: 520 }}>
        {/* Rating */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28, opacity: headerProgress }}>
          <div style={{ width: 90, height: 90, borderRadius: 20, background: 'rgba(200,150,90,0.1)', border: '2px solid rgba(200,150,90,0.3)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ fontSize: 36, fontWeight: 900, color: '#c8965a' }}>{ratingDisplay.toFixed(1)}</div>
          </div>
          <div>
            <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.5)' }}>豆瓣评分</div>
            <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} style={{ width: 20, height: 20, fontSize: 20 }}>
                  {i < Math.round(p.rating / 2) ? '⭐' : '☆'}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Shot analysis */}
        <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginBottom: 14, fontWeight: 600, letterSpacing: '0.1em' }}>镜头语言分析</div>
        {p.shots.map((shot, i) => {
          const start = shotStart + i * shotInterval;
          const progress = spring({ frame: frame - start, fps, from: 0, to: 1, config: { damping: 14, stiffness: 120 } });
          return (
            <div key={i} style={{
              padding: '18px 22px', background: 'rgba(255,255,255,0.03)',
              borderRadius: 14, border: '1px solid rgba(255,255,255,0.06)',
              marginBottom: 12, opacity: progress, transform: `translateX(${(1 - progress) * 20}px)`,
            }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#c8965a', marginBottom: 10 }}>{shot.icon} {shot.type}</div>
              <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>{shot.description}</div>
            </div>
          );
        })}

        {/* Themes */}
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginBottom: 12, fontWeight: 600 }}>🎭 核心主题</div>
          {p.themes.map((theme, i) => {
            const opacity = interpolate(frame, [themeStart + i * 22, themeStart + i * 22 + 18], [0, 1], { extrapolateRight: 'clamp' });
            return (
              <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, opacity }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#c8965a', marginTop: 13, flexShrink: 0 }} />
                <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>{theme}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Trivia bar */}
      <div style={{ position: 'absolute', bottom: 50, left: 70, right: 50, opacity: triviaOpacity }}>
        <div style={{ padding: '18px 28px', background: '#111', border: '1px solid rgba(200,150,90,0.15)', borderRadius: 16, textAlign: 'center' }}>
          <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>🎞️ {p.trivia}</span>
        </div>
      </div>

      {/* Progress */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`, height: 3, background: 'linear-gradient(90deg, #c8965a, #e8c080)', zIndex: 11 }} />
    </AbsoluteFill>
  );
};
